import {
  getBindings,
  onBindingsChanged,
  type BindableAction,
  type KeyBindings,
} from './bindings';

export type InputButton =
  | 'longPass'
  | 'highPass'
  | 'shortPass'
  | 'shot'
  | 'switch'
  | 'special'
  | 'sprint'
  | 'dribble'
  | 'teamPressure'
  | 'keeperRush'
  | 'shirtPull'
  | 'flyingKick'
  | 'punch'
  | 'allOut'
  | 'feint'
  | 'shield'
  | 'chip'
  | 'select'
  | 'start';

export interface JoinedController {
  id: string;
  kind: 'keyboard' | 'gamepad';
  side: -1 | 0 | 1;
}

export interface TickInput {
  readonly kind: 'keyboard' | 'gamepad';
  readonly x: number;
  readonly y: number;
  readonly held: ReadonlySet<InputButton>;
  readonly pressed: ReadonlySet<InputButton>;
  readonly released: ReadonlySet<InputButton>;
}

export interface TickInputSource {
  sample(controllers: readonly JoinedController[]): ReadonlyMap<string, TickInput>;
  dispose?(): void;
}

const EMPTY_BUTTONS: ReadonlySet<InputButton> = new Set();

export function emptyTickInput(kind: 'keyboard' | 'gamepad'): TickInput {
  return {
    kind,
    x: 0,
    y: 0,
    held: EMPTY_BUTTONS,
    pressed: EMPTY_BUTTONS,
    released: EMPTY_BUTTONS,
  };
}

/** Fixed, because they are shell controls rather than football ones. */
const SYSTEM_KEYS = new Map<string, InputButton>([
  ['F1', 'select'],
  ['Enter', 'start'],
]);

/** The player-facing half of the map, rebuilt whenever the bindings change. */
const BUTTON_ACTIONS: readonly (BindableAction & InputButton)[] = [
  'shortPass',
  'longPass',
  'highPass',
  'shot',
  'sprint',
  'dribble',
  'switch',
  'special',
  'teamPressure',
  'keeperRush',
  'shirtPull',
  'flyingKick',
  'punch',
  'allOut',
];

interface KeyMap {
  buttons: Map<string, InputButton>;
  up: string;
  down: string;
  left: string;
  right: string;
  all: Set<string>;
}

function buildKeyMap(bindings: KeyBindings): KeyMap {
  const buttons = new Map<string, InputButton>(SYSTEM_KEYS);
  for (const action of BUTTON_ACTIONS) {
    const code = bindings[action];
    if (code) buttons.set(code, action);
  }
  const all = new Set<string>(buttons.keys());
  for (const code of [bindings.up, bindings.down, bindings.left, bindings.right]) {
    if (code) all.add(code);
  }
  return {
    buttons,
    up: bindings.up,
    down: bindings.down,
    left: bindings.left,
    right: bindings.right,
    all,
  };
}

/**
 * Event-backed keyboard state. Press/release latches are drained by the simulation tick,
 * so a complete tap between two ticks still reaches the controller once.
 */
export class KeyboardInputBuffer {
  private readonly down = new Set<string>();
  private readonly pressed = new Set<string>();
  private readonly released = new Set<string>();
  private keys: KeyMap;
  private readonly stopListening: () => void;

  constructor(bindings: KeyBindings = getBindings()) {
    this.keys = buildKeyMap(bindings);
    // A rebind during a match takes effect on the next key press, not the next match.
    this.stopListening = onBindingsChanged((next) => {
      this.keys = buildKeyMap(next);
      this.reset();
    });
  }

  /** Whether this key is bound to anything at all. */
  handles(code: string): boolean {
    return this.keys.all.has(code);
  }

  keyDown(code: string): void {
    if (!this.keys.all.has(code)) return;
    if (!this.down.has(code)) this.pressed.add(code);
    this.down.add(code);
  }

  keyUp(code: string): void {
    if (!this.keys.all.has(code)) return;
    if (this.down.has(code) || this.pressed.has(code)) this.released.add(code);
    this.down.delete(code);
  }

  sample(): TickInput {
    const heldButtons = new Set<InputButton>();
    const pressedButtons = new Set<InputButton>();
    const releasedButtons = new Set<InputButton>();
    for (const [code, button] of this.keys.buttons) {
      if (this.down.has(code)) heldButtons.add(button);
      if (this.pressed.has(code)) pressedButtons.add(button);
      if (this.released.has(code)) releasedButtons.add(button);
    }

    // A direction tapped wholly between ticks is deliberately represented for one tick.
    const active = (code: string): boolean => this.down.has(code) || this.pressed.has(code);
    const x = Number(active(this.keys.right)) - Number(active(this.keys.left));
    const y = Number(active(this.keys.up)) - Number(active(this.keys.down));
    const length = Math.hypot(x, y);

    this.pressed.clear();
    this.released.clear();
    return {
      kind: 'keyboard',
      x: length > 0 ? x / length : 0,
      y: length > 0 ? y / length : 0,
      held: heldButtons,
      pressed: pressedButtons,
      released: releasedButtons,
    };
  }

  reset(): void {
    this.down.clear();
    this.pressed.clear();
    this.released.clear();
  }

  dispose(): void {
    this.stopListening();
    this.reset();
  }
}

interface BrowserInputOptions {
  eventTarget?: Pick<Window, 'addEventListener' | 'removeEventListener'>;
  gamepads?: () => readonly (Gamepad | null)[];
}

/**
 * Samples only devices already joined by the shell.
 *
 * In particular, this class never treats `navigator.getGamepads()` as enumeration. The shell's
 * press-a-button join flow is the sole authority for which gamepad ids enter the match.
 */
export class BrowserInputSource implements TickInputSource {
  private readonly keyboard = new KeyboardInputBuffer();
  private readonly previousGamepadButtons = new Map<string, Set<InputButton>>();
  private readonly eventTarget: Pick<Window, 'addEventListener' | 'removeEventListener'>;
  private readonly getGamepads: () => readonly (Gamepad | null)[];
  private readonly onKeyDown: (event: Event) => void;
  private readonly onKeyUp: (event: Event) => void;

  constructor(options: BrowserInputOptions = {}) {
    this.eventTarget = options.eventTarget ?? window;
    this.getGamepads =
      options.gamepads ??
      (() => (navigator.getGamepads ? Array.from(navigator.getGamepads()) : []));
    // The buffer owns the live binding map, so it decides what counts as a game key; this
    // only has to know whether to stop the browser doing something else with it.
    this.onKeyDown = (event): void => {
      const keyboardEvent = event as KeyboardEvent;
      if (!this.keyboard.handles(keyboardEvent.code)) return;
      keyboardEvent.preventDefault();
      this.keyboard.keyDown(keyboardEvent.code);
    };
    this.onKeyUp = (event): void => {
      const keyboardEvent = event as KeyboardEvent;
      if (!this.keyboard.handles(keyboardEvent.code)) return;
      keyboardEvent.preventDefault();
      this.keyboard.keyUp(keyboardEvent.code);
    };
    this.eventTarget.addEventListener('keydown', this.onKeyDown);
    this.eventTarget.addEventListener('keyup', this.onKeyUp);
  }

  sample(controllers: readonly JoinedController[]): ReadonlyMap<string, TickInput> {
    const result = new Map<string, TickInput>();
    const keyboard = keyboardJoined(controllers) ? this.keyboard.sample() : null;
    const gamepads = this.getGamepads();
    for (const controller of controllers) {
      if (controller.side === 0) continue;
      if (controller.kind === 'keyboard') {
        result.set(controller.id, keyboard ?? emptyTickInput('keyboard'));
        continue;
      }
      const index = gamepadIndex(controller.id);
      const gamepad = index === null ? null : gamepads[index] ?? null;
      result.set(controller.id, this.sampleGamepad(controller.id, gamepad));
    }
    return result;
  }

  dispose(): void {
    this.eventTarget.removeEventListener('keydown', this.onKeyDown);
    this.eventTarget.removeEventListener('keyup', this.onKeyUp);
    // dispose, not reset: the buffer also subscribed to the rebinding notices, and that list
    // outlives the match. Only clearing its keys left every finished match's buffer on it for
    // ever, so changing a key after five matches rebuilt five abandoned keyboards.
    this.keyboard.dispose();
    this.previousGamepadButtons.clear();
  }

  private sampleGamepad(id: string, gamepad: Gamepad | null): TickInput {
    if (!gamepad) {
      this.previousGamepadButtons.delete(id);
      return emptyTickInput('gamepad');
    }

    const held = gamepadButtons(gamepad);
    const previous = this.previousGamepadButtons.get(id) ?? new Set<InputButton>();
    const pressed = new Set([...held].filter((button) => !previous.has(button)));
    const released = new Set([...previous].filter((button) => !held.has(button)));
    this.previousGamepadButtons.set(id, held);

    let x = gamepad.axes[0] ?? 0;
    let y = -(gamepad.axes[1] ?? 0);
    if (buttonDown(gamepad, 14)) x -= 1;
    if (buttonDown(gamepad, 15)) x += 1;
    if (buttonDown(gamepad, 12)) y += 1;
    if (buttonDown(gamepad, 13)) y -= 1;
    const length = Math.hypot(x, y);
    // gamedefines.hpp:31 — retain the original's deliberately large analogue deadzone.
    if (length < 0.75) {
      x = 0;
      y = 0;
    } else {
      x /= length;
      y /= length;
    }
    return { kind: 'gamepad', x, y, held, pressed, released };
  }
}

function keyboardJoined(controllers: readonly JoinedController[]): boolean {
  return controllers.some((controller) => controller.kind === 'keyboard' && controller.side !== 0);
}

function gamepadIndex(id: string): number | null {
  const match = /^gamepad-(\d+)$/.exec(id);
  return match ? Number(match[1]) : null;
}

function buttonDown(gamepad: Gamepad, index: number): boolean {
  const button = gamepad.buttons[index];
  return button?.pressed === true || (button?.value ?? 0) > 0.5;
}

/**
 * Which pad button does what.
 *
 * Indices are the browser's standard gamepad layout, so 0-3 are the four face buttons, 4-7 the
 * shoulders and triggers, 8-9 select and start, and 10-11 the two sticks pressed in.
 *
 * The last two are ours and not the original's. It puts both orders to the rest of the side on
 * face buttons it is already using for the shot and the through ball (`gamepad.cpp:70-74`),
 * which works there only because the same press means one thing while attacking and the other
 * while defending — and its own author left a note beside that saying the arrangement is a bug.
 * A keyboard here has a separate key for each, so a pad gets a separate button for each: the two
 * sticks pressed in, which nothing else uses. Without them a pad could not squeeze the man on
 * the ball or send the goalkeeper out at all, and nothing said so.
 *
 * SEVEN THINGS A KEYBOARD CAN DO AND A PAD CANNOT: the shirt pull, the flying kick, the punch,
 * the all-out order, the body swerve, shielding the ball and the chip. THE STANDARD PAD IS
 * FULL — the last three are football rather than orders or offences, and they are off it for
 * the same reason, which is that there is nowhere left to put them. Twelve of its indices are
 * already spoken for above, and 12 to 15 are the four directions of the d-pad, which this same
 * file reads as movement — there is no sixteenth button on the layout every browser reports.
 * Doubling up a face button is exactly the arrangement the original's own author called a bug,
 * and doing it for a straight red card would be worse than not having the key: nobody wants to
 * be sent off because a shot came out as a punch. The controls page marks each of these seven by
 * checking `GAMEPAD_BUTTON_ACTIONS`, below, rather than naming them by hand a second time.
 */
const GAMEPAD_BUTTON_MAP: ReadonlyArray<readonly [number, InputButton]> = [
  [3, 'longPass'],
  [1, 'highPass'],
  [0, 'shortPass'],
  [2, 'shot'],
  [4, 'switch'],
  [6, 'special'],
  [5, 'sprint'],
  [7, 'dribble'],
  [8, 'select'],
  [9, 'start'],
  [10, 'teamPressure'],
  [11, 'keeperRush'],
];

/**
 * Every action reachable from a gamepad at all — the controls page's "not on a gamepad" badges
 * check against this, so they cannot go stale the way a hand-typed warning already had.
 */
export const GAMEPAD_BUTTON_ACTIONS: ReadonlySet<InputButton> = new Set(
  GAMEPAD_BUTTON_MAP.map(([, action]) => action),
);

function gamepadButtons(gamepad: Gamepad): Set<InputButton> {
  const result = new Set<InputButton>();
  for (const [index, button] of GAMEPAD_BUTTON_MAP) {
    if (buttonDown(gamepad, index)) result.add(button);
  }
  return result;
}
